<template>
    <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
        <div
            class="card-head box-shadow bg-white p-15 p-sm-20 p-md-25">
            <h4 class="mb-15">{{ $t('feature.auth.$invitation.pending_invitations') }}</h4>
            <p>{{ $t('feature.auth.$invitation.pending_invitations_message') }}</p>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
            <SpinnerLoading v-if="invites === undefined" class="mt-50" />
            <div class="table-responsive" v-else>
                <table class="table text-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                {{ $t('form.email.label') }}
                            </th>
                            <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                                {{ $t('feature.auth.$invitation.invitation_expires_date') }}
                            </th>
                            <th scope="col"
                                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <!-- CENTER ROW -->
                        <tr v-for="invite in invites" :key="'tr_invite_' + invite.recipient_email">
                            <td class="shadow-none lh-1 fw-medium text-primary">
                                {{ invite.recipient_email }}
                            </td>
                            <td class="shadow-none lh-1 fw-medium text-black-emphasis"
                                :class="[{ 'text-muted': expired(invite) }]">
                                {{ YYYYMMDD_HHmm(invite.expires_at) }}
                            </td>

                            <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                                <SpinnerLoading v-if="loading === invite.recipient_email" class="h-auto"
                                    style="justify-content: end;" />
                                <template v-else>

                                    <button v-if="expired(invite)"
                                        class="default-btn transition fw-medium text-white px-3 py-2 fs-md-15 fs-lg-16"
                                        v-on:click="resend(invite)">
                                        {{ $t('feature.auth.$invitation.resend_invitation') }}
                                    </button>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
    <AlertModal id="successModal" :title="$t('feature.auth.invitation')" :dismissible="false"
        :message="$t('feature.auth.$invitation.send_success_message')" @confirm="redirect" />
    <AlertModal id="errorModal" :title="$t('feature.auth.invitation')" :message="errorMessage" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { Modal } from "bootstrap";
import API from "@/services/api/api.service";
import stateStore from "@/utils/store";
import { Zone } from "@/utils/models/zone.model";
import DateFormatUtils from "@/utils/methods/date_formats";
import dayjs from "dayjs";
import AlertModal from "@/components/Widgets/AlertModal.vue";

const PAGE_SIZE = 10;

interface Invite {
    recipient_email: string,
    expires_at: Date
}

export default defineComponent({
    name: "InvitationList",
    data: () => {
        return {
            invites: undefined,
            currentPage: 1,
            lastPage: 1,
            total: 0,
            perPage: PAGE_SIZE,
            successModal: null,
            errorModal: null,
            selectedZone: null,
            loading: undefined,
            errorMessage: undefined,
        } as {
            invites?: Invite[],
            currentPage: number,
            lastPage: number,
            total: number,
            perPage: number,
            successModal: Modal | null,
            errorModal: Modal | null,
            selectedZone: Zone | null;
            loading: undefined | string;
            errorMessage: undefined | string;
        }
    },
    mounted() {
        this.loadInvites(1);
        this.successModal = new Modal('#successModal', {});
        this.errorModal = new Modal('#errorModal', {});
    },
    computed: {
        isAdmin() { return stateStore.isAdmin() },
        YYYYMMDD_HHmm() {
            return DateFormatUtils.YYYYMMDD_HHmm;
        },
    },
    methods: {
        loadInvites(page: number) {
            this.invites = undefined;
            API.getInvites({ page }).then((res) => {
                this.invites = res.data.data;
                this.total = res.data.total;
                this.lastPage = res.data.last_page;
            })
        },
        setPage(page: number) {
            this.currentPage = page;
            this.loadInvites(page);
        },
        show(zone: Zone) {
            this.$router.push({ path: `/zone/${zone.id}/edit` });
        },
        expired(invite: Invite): boolean {
            return dayjs().isAfter(invite.expires_at);
        },
        resend(invite: Invite) {

            this.errorMessage = undefined;
            this.loading = invite.recipient_email;
            console.log('CREATE');

            API.sendInvite({ email: invite.recipient_email ?? '' }).then((response) => {
                console.log('RES', response);
                if (response.data.success === true) {
                    this.successModal?.show();
                } else {
                    console.error('RESEND_INVITE_ERROR');
                    this.errorMessage = response.data?.message;
                    this.errorModal?.show();
                }
            }).catch((error) => {
                console.error('RESEND_INVITE_ERROR', error);
                this.errorMessage = error.response?.data?.message;
                this.errorModal?.show();
            }).finally(() => {
                this.loading = undefined;
            });
        },
        redirect() {
            this.$router.push('/');
        },

    },
    components: {
        SpinnerLoading,
        AlertModal
    }
});
</script>